import { Add, Delete } from '@mui/icons-material';
import { Box, Typography, Button, IconButton, Paper, Divider, Tabs, Tab } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { useMemo, useState, type SyntheticEvent } from 'react';
import { useSelector } from 'react-redux';

import FormSkeleton from './formSkeleton';
import StepNote from './stepNote';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { changeTabIfValid } from './tabValidation';
import { BooleanValue } from 'constant';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    useCitizenship,
    useCountries,
    useRegions,
    useWorkPermits,
    useOccupationsByCountryCode,
    useWorkExperience,
    useSpouseWorkExperience,
    useSaveWorkExperience,
    useSaveSpouseWorkExperience,
} from 'hooks';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import type { PartnerWorkExperienceInterface, WorkExperience } from 'types';
import {
    chosenCountryName,
    countryNameOptions,
    enabledMarketFields,
    isDestinationCountry,
    percentTimeTooltipKey,
    rowCountryEnabled,
    type MarketFields,
} from 'utils/countries';
import {
    formatDayjsDate,
    generateOptionFromObject,
    generateOptionsFromString,
    generateSelectOptions,
} from 'utils/dataFormatter';
import { createWorkExperienceValidationSchema } from 'utils/validations/workExperienceSchema';

const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);
const percentTime = Array(10)
    .fill(0)
    .map((_, i) => String((i + 1) * 10))
    .reverse();

const emptyWorkExperience: Partial<WorkExperience> = {
    spouse_id: null,
    country: '',
    region: '',
    start: '',
    end: '',
    current: false,
    self_employed: false,
    percent_time: 0,
    work_permit: '',
    zip: '',
    occupation_au: '',
    occupation_au_en: '',
    occupation_au_id: null,
    occupation_ca: '',
    occupation_ca_en: '',
    occupation_ca_id: null,
    occupation_uk: '',
    occupation_uk_en: '',
    occupation_uk_id: null,
};

const emptyPartnerWorkExperience: Partial<PartnerWorkExperienceInterface> = {
    country: '',
    region: '',
    start: '',
    end: '',
    current: false,
    work_permit: '',
};

interface WorkExperienceStepProps {
    hasSpouse: string;
    isLastStep: boolean;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

const WorkExperienceStep = ({
    hasSpouse: hasSpouseValue,
    isLastStep,
    onNext,
    onBack,
}: WorkExperienceStepProps) => {
    const { translate } = useTranslate();
    const { codes } = useEnabledCountries();
    const validationSchema = useMemo(() => createWorkExperienceValidationSchema(codes), [codes]);
    const user = useSelector((state: RootState) => state.user.entity);
    const hasSpouse = hasSpouseValue === 'Yes';

    const [activeTab, setActiveTab] = useState(0);

    const { data: userWork, isLoading: userLoading } = useWorkExperience();
    const { data: spouseWork, isLoading: spouseLoading } = useSpouseWorkExperience(hasSpouse);
    const saveUserWork = useSaveWorkExperience();
    const saveSpouseWork = useSaveSpouseWorkExperience();
    const { data: citizenship = [], isLoading: citizenshipLoading } = useCitizenship();
    const citizenshipCountries = citizenship
        .map((item) => item.country)
        .filter((country): country is string => Boolean(country));
    // AssessmentV2 WorkExperienceV2: permit only for AU/CA/UK when the user is not a citizen there.
    const showEmployerPermit = (country?: string | null) =>
        isDestinationCountry(country) && !citizenshipCountries.includes(country ?? '');

    const { data: countries = [] } = useCountries();
    const { data: regions } = useRegions();
    const { data: workPermits } = useWorkPermits();

    const countryOptions = generateOptionsFromString(countries || []);
    const spouseCountryOptions = (country?: string | null) =>
        generateOptionsFromString(countryNameOptions(codes, country));
    const soleSpouseCountry = chosenCountryName(codes) ?? '';
    const workPermitOptions = generateOptionsFromString(workPermits || []);
    const percentTimeOptions = generateOptionsFromString(percentTime);
    const initialValues = {
        has_experience: userWork && userWork.length > 0,
        user_work: userWork && userWork.length > 0 ? userWork : [],
        spouse_work: (spouseWork ?? []).map((row) => ({
            ...row,
            country: row.country || soleSpouseCountry,
        })),
    };

    const handleSubmit = async (values: {
        user_work: WorkExperience[];
        spouse_work: PartnerWorkExperienceInterface[];
    }) => {
        try {
            const userWorkWithId = values.user_work.map((work) => ({
                ...work,
                user_id: user?.id || 0,
                spouse_id: null,
            }));
            await saveUserWork.mutateAsync(userWorkWithId);

            if (hasSpouse && values.spouse_work.length > 0) {
                const spouseWorkWithId = values.spouse_work;
                await saveSpouseWork.mutateAsync(spouseWorkWithId);
            }

            onNext({
                user_work: userWorkWithId,
                spouse_work: hasSpouse ? values.spouse_work : [],
            });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (userLoading || citizenshipLoading || (hasSpouse && spouseLoading)) {
        return <FormSkeleton fields={10} />;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({
                    values,
                    isValid,
                    isSubmitting,
                    setFieldValue,
                    setValues,
                    validateForm,
                    setTouched,
                }) => {
                    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
                        void changeTabIfValid({
                            nextTab: newValue,
                            activeTab,
                            // When has_experience is false, only that field matters for the user tab.
                            tabFieldKeys: [
                                values.has_experience
                                    ? ['has_experience', 'user_work']
                                    : ['has_experience'],
                                ['spouse_work'],
                            ],
                            values,
                            validateForm,
                            setTouched,
                            setActiveTab,
                        });
                    };

                    const isSaving =
                        isSubmitting || saveUserWork.isPending || saveSpouseWork.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                {hasSpouse && (
                                    <Tabs
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        sx={{ mb: 3 }}
                                    >
                                        <Tab label="Your Work Experience" />
                                        <Tab label="Spouse Work Experience" />
                                    </Tabs>
                                )}

                                {activeTab === 0 && (
                                    <>
                                        <StepNote messageKey="workExperienceNote" />
                                        <FormField
                                            type="radio"
                                            row
                                            name={`has_experience`}
                                            label="recentWorkExperience"
                                            options={booleanOptions}
                                            onChange={(value: unknown) => {
                                                const boolValue =
                                                    value === true || value === 'true';
                                                return setValues(
                                                    {
                                                        ...values,
                                                        has_experience: boolValue,
                                                        user_work: boolValue
                                                            ? [
                                                                  {
                                                                      ...emptyWorkExperience,
                                                                  } as WorkExperience,
                                                              ]
                                                            : [],
                                                    },
                                                    true
                                                );
                                            }}
                                        />
                                        <FieldArray name="user_work">
                                            {({ push, remove }) => (
                                                <Box>
                                                    {values.user_work.map((work, index) => {
                                                        const regionOptions =
                                                            generateOptionsFromString(
                                                                work.country
                                                                    ? (regions?.[work.country] ??
                                                                          [])
                                                                    : []
                                                            );
                                                        return (
                                                            <Paper
                                                                key={index}
                                                                elevation={1}
                                                                sx={{
                                                                    p: { xs: 2, sm: 3 },
                                                                    mb: 1,
                                                                    mt: 2,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems: 'center',
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{ fontSize: '1.1rem' }}
                                                                    >
                                                                        Job {index + 1}
                                                                    </Typography>
                                                                    {values.user_work.length >
                                                                        1 && (
                                                                        <IconButton
                                                                            onClick={() =>
                                                                                remove(index)
                                                                            }
                                                                            color="error"
                                                                            size="small"
                                                                        >
                                                                            <Delete />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                                <Divider sx={{ mb: 2 }} />

                                                                <FormGridLayout>
                                                                    <FormField
                                                                        type="radio"
                                                                        row
                                                                        name={`user_work.${index}.self_employed`}
                                                                        label="areYouSelfEmployed"
                                                                        options={booleanOptions}
                                                                    />
                                                                    <FormField
                                                                        type="radio"
                                                                        row
                                                                        name={`user_work.${index}.current`}
                                                                        label="currentEmployment"
                                                                        options={booleanOptions}
                                                                        onChange={(
                                                                            value: unknown
                                                                        ) => {
                                                                            const boolValue =
                                                                                value === 'true' ||
                                                                                value === true;
                                                                            setFieldValue(
                                                                                `user_work.${index}.current`,
                                                                                boolValue
                                                                            );
                                                                            // Clear the end date when marked current/ongoing.
                                                                            setFieldValue(
                                                                                `user_work.${index}.end`,
                                                                                boolValue
                                                                                    ? null
                                                                                    : values
                                                                                          .user_work[
                                                                                          index
                                                                                      ].end
                                                                            );
                                                                        }}
                                                                    />
                                                                    <FormField
                                                                        required
                                                                        type="monthyear"
                                                                        name={`user_work.${index}.start`}
                                                                        label="startDate"
                                                                    />
                                                                    <FormField
                                                                        required={!work.current}
                                                                        type="monthyear"
                                                                        name={`user_work.${index}.end`}
                                                                        label="endDate"
                                                                        minDate={formatDayjsDate(
                                                                            work.start
                                                                        )}
                                                                        disabled={work.current}
                                                                    />
                                                                    <FormField
                                                                        required
                                                                        type="autosuggest"
                                                                        name={`user_work.${index}.country`}
                                                                        label="country"
                                                                        options={countryOptions}
                                                                        onChange={(value) => {
                                                                            const nextCountry =
                                                                                typeof value ===
                                                                                'string'
                                                                                    ? value
                                                                                    : '';
                                                                            setFieldValue(
                                                                                `user_work.${index}.country`,
                                                                                value
                                                                            );
                                                                            setFieldValue(
                                                                                `user_work.${index}.region`,
                                                                                ''
                                                                            );
                                                                            if (
                                                                                !showEmployerPermit(
                                                                                    nextCountry
                                                                                )
                                                                            ) {
                                                                                setFieldValue(
                                                                                    `user_work.${index}.work_permit`,
                                                                                    ''
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                    {isDestinationCountry(
                                                                        work.country
                                                                    ) && (
                                                                        <FormField
                                                                            required
                                                                            type="autosuggest"
                                                                            name={`user_work.${index}.region`}
                                                                            label="region"
                                                                            options={regionOptions}
                                                                        />
                                                                    )}
                                                                    {rowCountryEnabled(
                                                                        codes,
                                                                        work.country,
                                                                        'AU'
                                                                    ) && (
                                                                        <FormField
                                                                            name={`user_work.${index}.zip`}
                                                                            label="zipLabel"
                                                                            tooltip="zipTooltipExperience"
                                                                        />
                                                                    )}
                                                                    {showEmployerPermit(
                                                                        work.country
                                                                    ) && (
                                                                        <FormField
                                                                            type="autosuggest"
                                                                            name={`user_work.${index}.work_permit`}
                                                                            label="employerPermit"
                                                                            options={
                                                                                workPermitOptions
                                                                            }
                                                                        />
                                                                    )}
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`user_work.${index}.percent_time`}
                                                                        label="percentOfFulltimeWork"
                                                                        tooltip={percentTimeTooltipKey(
                                                                            codes
                                                                        )}
                                                                        options={percentTimeOptions}
                                                                    />
                                                                    {enabledMarketFields(codes).map(
                                                                        (market) => (
                                                                            <OccupationField
                                                                                key={market.code}
                                                                                index={index}
                                                                                market={market}
                                                                                setFieldValue={
                                                                                    setFieldValue
                                                                                }
                                                                            />
                                                                        )
                                                                    )}
                                                                </FormGridLayout>
                                                            </Paper>
                                                        );
                                                    })}

                                                    {values.has_experience && (
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<Add />}
                                                            onClick={() =>
                                                                push({
                                                                    ...emptyWorkExperience,
                                                                } as WorkExperience)
                                                            }
                                                            fullWidth
                                                            sx={{ mb: 3, mt: 2 }}
                                                        >
                                                            {translate('addWorkExperience')}
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                        </FieldArray>
                                    </>
                                )}

                                {activeTab === 1 && hasSpouse && (
                                    <FieldArray name="spouse_work">
                                        {({ push, remove }) => {
                                            return (
                                                <Box>
                                                    <StepNote messageKey="partnerWorkExperienceNote" />
                                                    {values.spouse_work.map((work, index) => {
                                                        const regionOptions =
                                                            generateOptionsFromString(
                                                                work.country
                                                                    ? (regions?.[work.country] ??
                                                                          [])
                                                                    : []
                                                            );
                                                        return (
                                                            <Paper
                                                                key={index}
                                                                elevation={1}
                                                                sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems: 'center',
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{ fontSize: '1.1rem' }}
                                                                    >
                                                                        Spouse Job {index + 1}
                                                                    </Typography>
                                                                    {values.spouse_work.length >
                                                                        1 && (
                                                                        <IconButton
                                                                            onClick={() =>
                                                                                remove(index)
                                                                            }
                                                                            color="error"
                                                                            size="small"
                                                                        >
                                                                            <Delete />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                                <Divider sx={{ mb: 2 }} />

                                                                <FormGridLayout>
                                                                    <FormField
                                                                        type="monthyear"
                                                                        name={`spouse_work.${index}.start`}
                                                                        label="startDate"
                                                                    />
                                                                    <FormField
                                                                        type="radio"
                                                                        row
                                                                        name={`spouse_work.${index}.current`}
                                                                        label="currentEmployment"
                                                                        options={booleanOptions}
                                                                        onChange={(
                                                                            value: unknown
                                                                        ) => {
                                                                            const boolValue =
                                                                                value === 'true' ||
                                                                                value === true;
                                                                            setFieldValue(
                                                                                `spouse_work.${index}.current`,
                                                                                boolValue
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_work.${index}.end`,
                                                                                boolValue
                                                                                    ? null
                                                                                    : values
                                                                                          .spouse_work[
                                                                                          index
                                                                                      ].end
                                                                            );
                                                                        }}
                                                                    />
                                                                    <FormField
                                                                        type="monthyear"
                                                                        name={`spouse_work.${index}.end`}
                                                                        label="endDate"
                                                                        minDate={formatDayjsDate(
                                                                            work.start
                                                                        )}
                                                                        disabled={work.current}
                                                                    />
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`spouse_work.${index}.country`}
                                                                        label="country"
                                                                        options={spouseCountryOptions(
                                                                            work.country
                                                                        )}
                                                                        onChange={(value) => {
                                                                            setFieldValue(
                                                                                `spouse_work.${index}.country`,
                                                                                value
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_work.${index}.region`,
                                                                                ''
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_work.${index}.work_permit`,
                                                                                ''
                                                                            );
                                                                        }}
                                                                    />
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`spouse_work.${index}.region`}
                                                                        label="region"
                                                                        options={regionOptions}
                                                                        disabled={!work.country}
                                                                    />
                                                                    {isDestinationCountry(
                                                                        work.country
                                                                    ) && (
                                                                        <FormField
                                                                            type="autosuggest"
                                                                            name={`spouse_work.${index}.work_permit`}
                                                                            label="employerPermit"
                                                                            options={
                                                                                workPermitOptions
                                                                            }
                                                                        />
                                                                    )}
                                                                </FormGridLayout>
                                                            </Paper>
                                                        );
                                                    })}

                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        startIcon={<Add />}
                                                        onClick={() =>
                                                            push({
                                                                ...emptyPartnerWorkExperience,
                                                                country: soleSpouseCountry,
                                                            })
                                                        }
                                                        sx={{ mb: 3 }}
                                                    >
                                                        Add Spouse Work Experience
                                                    </Button>
                                                </Box>
                                            );
                                        }}
                                    </FieldArray>
                                )}

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

const OccupationField = ({
    index,
    market,
    setFieldValue,
}: {
    index: number;
    market: MarketFields;
    setFieldValue: (field: string, value: unknown) => void;
}) => {
    const { data: occupationList = [], isFetching } = useOccupationsByCountryCode(market.code);
    const occupationOptions = generateSelectOptions(occupationList ?? [], 'name', 'id');

    return (
        <FormField
            required
            type="aiselect"
            kind="occupation"
            countryCode={market.code}
            name={`user_work.${index}.${market.occupationIdKey}`}
            label={market.occupationLabelKey}
            options={occupationOptions}
            disabled={isFetching}
            onChange={(value: unknown) => {
                const selectedId = value === '' || value == null ? null : Number(value);
                const selected = (occupationList ?? []).find((item) => item.id === selectedId);
                setFieldValue(`user_work.${index}.${market.occupationIdKey}`, selectedId);
                setFieldValue(
                    `user_work.${index}.${market.occupationNameKey}`,
                    selected?.name ?? ''
                );
                setFieldValue(
                    `user_work.${index}.${market.occupationNameEnKey}`,
                    selected?.name ?? ''
                );
            }}
        />
    );
};

export default WorkExperienceStep;
